import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { Box, Checkbox, FormControlLabel, IconButton, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EventDetails } from '../../../backendsdk';
import { TrackedButton as Button, TrackedDialog as Dialog } from '../../../components/TrackedComponents';
import { SetAlert } from '../../../hooks/alert';
import useApi from '../../../hooks/api';
import useProfile from '../../../hooks/profile';
import { OE_ROLES } from '../Users/UsersComponent';
import SingleStreamNoOverlay from './EventVideoImages/1-stream-no-overlay.svg';
import SingleStreamOverlay from './EventVideoImages/1-stream-overlay.svg';
import DSNoOverlay from './EventVideoImages/DS-no-overlay.svg';
import DSOverlay from './EventVideoImages/DS-overlay.svg';
import FFNoOverlay from './EventVideoImages/FF-no-overlay.svg';
import FFOverlay from './EventVideoImages/FF-overlay.svg';
import ICNoOverlay from './EventVideoImages/IC-no-overlay.svg';
import ICOverlay from './EventVideoImages/IC-overlay.svg';

export interface DownloadEventVideoModalComponentProps {
    eventDetails?: EventDetails;
    eventId: number;
    onClose: () => void;
    isInCabinPrimary: boolean;
    setAlert: SetAlert;
    numberOfStreams: number;
    artifact: string;
}

const PIP_IMAGE_MAP: Record<number, Record<number, string>> = {
    1: {
        1: ICOverlay,
        0: ICNoOverlay,
    },
    0: {
        1: FFOverlay,
        0: FFNoOverlay,
    },
} as const;

const DownloadEventVideoModalComponent: React.FC<DownloadEventVideoModalComponentProps> = (
    props: DownloadEventVideoModalComponentProps,
) => {
    const [isPictureInPicture, setIsPictureInPicture] = useState<boolean>(false);
    const [addOverlay, setAddOverlay] = useState<boolean>(false);
    const [isInCabinPrimary, setIsInCabinPrimary] = useState<boolean>(props.isInCabinPrimary);
    const [unblur, setUnblur] = useState<boolean>(false);
    const { api } = useApi();
    const { t } = useTranslation();
    const { profile } = useProfile();

    const canUnblur =
        OE_ROLES.includes(profile.role) &&
        (props.eventDetails?.tags || []).map((tag) => tag.toLocaleLowerCase()).includes('maskreversibleblur');

    let image;
    if (props.numberOfStreams === 1) {
        image = addOverlay ? SingleStreamOverlay : SingleStreamNoOverlay;
    } else if (isPictureInPicture) {
        image = PIP_IMAGE_MAP[isInCabinPrimary ? 1 : 0][addOverlay ? 1 : 0];
    } else if (addOverlay) {
        image = DSOverlay;
    } else {
        image = DSNoOverlay;
    }
    const isQuickDownload = !isPictureInPicture && !addOverlay && (!canUnblur || !unblur);

    return (
        <Dialog id="event-video-modal" open={true} onClose={props.onClose}>
            <Box
                sx={{
                    backgroundColor: 'secondary.main',
                    top: 0,
                    p: 1,
                    zIndex: 2,
                    minHeight: 0,
                }}
            >
                <Box sx={{ position: 'absolute', display: 'flex', right: 0, top: 0, mt: 0.5, mr: 0.5 }}>
                    <IconButton
                        id="btn-close-modal"
                        data-testid="btn-close-modal"
                        size="small"
                        onClick={() => props.onClose()}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <DownloadIcon sx={{ mr: 0.5 }} />
                    <Typography>{t('content.events.video.download.send_download_request')}</Typography>
                </Box>
            </Box>
            <Box sx={{ px: 2 }}>
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', pt: 1 }}>
                    <FormControlLabel
                        disabled={props.numberOfStreams == 1}
                        control={
                            <Checkbox
                                id="show-pip-checkbox"
                                data-testid="show-pip-checkbox"
                                checked={isPictureInPicture}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    setIsPictureInPicture(e.target.checked)
                                }
                                disableRipple
                            />
                        }
                        label={<Typography>{t('content.events.video.download.picture_in_picture')}</Typography>}
                    />
                    <FormControlLabel
                        disabled={!isPictureInPicture || props.numberOfStreams == 1}
                        control={
                            <Checkbox
                                id="ic-primary-checkbox"
                                data-testid="ic-primary-checkbox"
                                checked={isInCabinPrimary}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    setIsInCabinPrimary(e.target.checked)
                                }
                                disableRipple
                            />
                        }
                        label={<Typography>{t('content.events.video.download.is_in_cabin_primary')}</Typography>}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                id="add-overlay-checkbox"
                                data-testid="add-overlay-checkbox"
                                checked={addOverlay}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAddOverlay(e.target.checked)}
                                disableRipple
                            />
                        }
                        label={<Typography>{t('content.events.video.download.add_overlay')}</Typography>}
                    />
                    {canUnblur ? (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    id="unblur-checkbox"
                                    data-testid="unblur-checkbox"
                                    checked={unblur}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUnblur(e.target.checked)}
                                    disableRipple
                                />
                            }
                            label={<Typography>{t('content.events.video.download.remove_blur')}</Typography>}
                        />
                    ) : null}
                </Box>
                <Box sx={{ width: '480px', display: 'flex', justifyContent: 'center' }}>
                    <img
                        src={image}
                        alt="Download video skeleton"
                        style={{ height: '180px', width: 'auto', objectFit: 'contain', marginTop: '10px' }}
                    />
                </Box>
                <Box sx={{ width: '100%', py: 1, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button
                        id="quick-dowload-btn"
                        data-testid="quick-dowload-btn"
                        variant="contained"
                        color="secondary"
                        disabled={!isQuickDownload}
                        onClick={() => {
                            window.open(props.artifact, '_blank', 'noopener,noreferrer');
                            props.onClose();
                        }}
                    >
                        {t('content.events.video.download.quick_download')}
                    </Button>
                    <Button
                        id="send-dowload-request-btn"
                        data-testid="send-dowload-request-btn"
                        variant="contained"
                        color="secondary"
                        disabled={isQuickDownload}
                        onClick={() =>
                            api
                                .apiV0EventEventIdDownloadPost({
                                    eventId: props.eventId,
                                    downloadVideoEventRequest: {
                                        picture_in_picture: isPictureInPicture,
                                        is_in_cabin_primary: isInCabinPrimary,
                                        add_overlay: addOverlay,
                                        unblur,
                                    },
                                })
                                .then(() => {
                                    props.setAlert({
                                        message: t('content.events.video.download.request_succeed'),
                                        type: 'success',
                                        duration: 6000,
                                    });
                                    props.onClose();
                                })
                                .catch(() => {
                                    props.setAlert({
                                        message: t('content.events.video.download.request_failed'),
                                        type: 'error',
                                        duration: 6000,
                                    });
                                })
                        }
                    >
                        {t('content.events.video.download.send_download_request')}
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
};

export default DownloadEventVideoModalComponent;
